import type { AdminRole, AuthenticatedAdmin } from "@utopia-well-done-ltd/contracts";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { ApiError, api, type OperatorSummary } from "../api/client.js";
import { can } from "../capabilities.js";

const ROLES: AdminRole[] = ["support", "kyc_officer", "finance", "admin", "super_admin"];
const label = (role: AdminRole) => role.replace("_", " ");

export function Operators({ admin }: { admin: AuthenticatedAdmin }) {
  const mayInitiate = can(admin.role, "admins.initiate");
  const mayApprove = can(admin.role, "admins.approve");
  const [operators, setOperators] = useState<OperatorSummary[]>([]);
  const [operatorId, setOperatorId] = useState("");
  const [nextRole, setNextRole] = useState<AdminRole>("support");
  const [roleReason, setRoleReason] = useState("");
  const [playerId, setPlayerId] = useState("");
  const [days, setDays] = useState(30);
  const [grantReason, setGrantReason] = useState("");
  const [grantStatus, setGrantStatus] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const result = await api.operators(signal);
      setOperators(result.operators);
      setOperatorId((current) => current || result.operators[0]?.id || "");
    } catch (caught) {
      if (!signal?.aborted)
        setError(caught instanceof ApiError ? caught.message : "Could not load operators.");
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const run = async (name: string, action: () => Promise<unknown>, success: string) => {
    setBusy(name);
    setError("");
    setNotice("");
    try {
      await action();
      setNotice(success);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Request failed.");
    } finally {
      setBusy("");
    }
  };

  const changeRole = (event: FormEvent) => {
    event.preventDefault();
    if (!operatorId || roleReason.trim().length < 20) return;
    void run(
      "role",
      () =>
        api.raiseAction({
          actionType: "admin.role_change",
          justification: roleReason.trim(),
          payload: { role: nextRole },
          targetId: operatorId,
        }),
      "Role change request raised for second-operator approval.",
    );
  };

  const setGrant = (enabled: boolean) => {
    if (!playerId || grantReason.trim().length < 3) return;
    void run(
      enabled ? "grant" : "revoke",
      async () => {
        await api.setModeratorGrant(playerId.trim(), { days, enabled, reason: grantReason.trim() });
        setGrantStatus("");
      },
      enabled ? "Player moderator access granted." : "Player moderator access revoked.",
    );
  };

  const inspectGrant = async () => {
    setBusy("inspect");
    setError("");
    try {
      const { grant } = await api.moderatorGrant(playerId.trim());
      setGrantStatus(
        !grant
          ? "No moderator grant."
          : grant.active
            ? `Active until ${new Date(grant.expiresAt).toLocaleString()}.`
            : "Grant is expired or revoked.",
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not read moderator access.");
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="body">
      {error ? (
        <p className="banner" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="banner banner-quiet" role="status">
          {notice}
        </p>
      ) : null}

      <div className="card">
        <div className="card-bar">
          <span className="label-14-strong">Operators</span>
          <span className="label-13 dim">{operators.length} accounts</span>
        </div>
        <div className="table-scroll">
          <table className="mobile-cards">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {operators.map((operator) => (
                <tr key={operator.id}>
                  <td>{operator.email}</td>
                  <td>{label(operator.role)}</td>
                  <td>{operator.disabledAt ? "Disabled" : "Active"}</td>
                  <td>{new Date(operator.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {mayInitiate ? (
        <div className="card">
          <div className="card-bar">
            <span className="label-14-strong">Operator role requests</span>
          </div>
          <p className="label-13 dim" style={{ margin: 16 }}>
            New operator creation stays disabled until user-owned email activation is available.
          </p>
          <form className="royal-spin-editor" onSubmit={changeRole}>
            <h3>Change role</h3>
            <label className="field">
              Operator
              <select
                className="input"
                value={operatorId}
                onChange={(e) => setOperatorId(e.target.value)}
              >
                {operators.map((operator) => (
                  <option key={operator.id} value={operator.id}>
                    {operator.email} · {label(operator.role)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              New role
              <select
                className="input"
                value={nextRole}
                onChange={(e) => setNextRole(e.target.value as AdminRole)}
              >
                {ROLES.map((role) => (
                  <option key={role} value={role}>
                    {label(role)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              Justification
              <textarea
                className="input textarea"
                maxLength={1000}
                value={roleReason}
                onChange={(e) => setRoleReason(e.target.value)}
              />
            </label>
            <button
              className="btn"
              disabled={busy === "role" || roleReason.trim().length < 20 || operatorId === admin.id}
              type="submit"
            >
              Raise role change
            </button>
          </form>
        </div>
      ) : null}

      {mayApprove ? (
        <div className="card">
          <div className="card-bar">
            <span className="label-14-strong">Player moderator access</span>
          </div>
          <div className="royal-spin-editor">
            <label className="field">
              Player ID
              <input
                className="input"
                value={playerId}
                onChange={(e) => {
                  setPlayerId(e.target.value);
                  setGrantStatus("");
                }}
              />
            </label>
            <label className="field">
              Grant duration
              <select
                className="input"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
              >
                {[1, 7, 30, 60, 90].map((value) => (
                  <option key={value} value={value}>
                    {value} days
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              Reason
              <textarea
                className="input textarea"
                maxLength={500}
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
              />
            </label>
            {grantStatus ? <p className="banner banner-quiet">{grantStatus}</p> : null}
            <div className="row-decide">
              <button
                className="btn btn-quiet"
                disabled={!playerId || Boolean(busy)}
                onClick={() => void inspectGrant()}
                type="button"
              >
                Check access
              </button>
              <button
                className="btn"
                disabled={!playerId || grantReason.trim().length < 3 || Boolean(busy)}
                onClick={() => setGrant(true)}
                type="button"
              >
                Grant access
              </button>
              <button
                className="btn btn-danger"
                disabled={!playerId || grantReason.trim().length < 3 || Boolean(busy)}
                onClick={() => setGrant(false)}
                type="button"
              >
                Revoke access
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
